// The single cover primitive for the library.
//
// Owns the 2:3 box, the responsive `srcSet`, the radius, the physical
// drop-shadow, and the fall-through to `CoverPlate` when there's no image — so
// no call site has to remember any of it, and a book with `cover_url: null`
// can never render as an empty rectangle.

import type { ShelfBook } from "../../lib/library-types";
import { coverSrcSet } from "../../lib/coverSrc";
import { CoverPlate, type PlateSize } from "./CoverPlate";

/** Per-slot radius, shadow rung and `sizes` hint. */
const SLOT: Record<PlateSize, { radius: string; shadow: string; sizes: string; width: number }> = {
  // The hero cover, ~210px in the comps.
  hero:      { radius: "rounded-lg",       shadow: "cover-shadow-lg", sizes: "210px", width: 400 },
  // The two tilted covers flanking it, ~150px.
  companion: { radius: "rounded-md",       shadow: "cover-shadow-md", sizes: "150px", width: 400 },
  // A seven-up rail slot on desktop, 38vw on mobile.
  rail:      { radius: "rounded-[7px]",    shadow: "cover-shadow-sm", sizes: "(max-width: 639px) 38vw, (max-width: 1023px) 25vw, 176px", width: 400 },
  // A six-column wall cell.
  wall:      { radius: "rounded-[6px]",    shadow: "cover-shadow-sm", sizes: "(max-width: 639px) 30vw, (max-width: 1023px) 22vw, 150px", width: 400 },
  // A chart / ranked-row thumbnail.
  row:       { radius: "rounded-[4px]",    shadow: "cover-shadow-sm", sizes: "68px", width: 128 },
};

interface BookCoverProps {
  book: ShelfBook;
  size: PlateSize;
  /** Degrees of tilt, for the hero's flanking covers. */
  rotate?: number;
  /**
   * `true` when adjacent text already names the book — the image then carries
   * `alt=""` so a screen reader doesn't announce the title twice. Cover-only
   * slots (mobile rails) must pass `false`.
   */
  decorative?: boolean;
  className?: string;
}

export function BookCover({ book, size, rotate, decorative = true, className = "" }: BookCoverProps) {
  const slot = SLOT[size];
  const tilt = rotate ? { transform: `translateY(14px) rotate(${rotate}deg)` } : undefined;

  return (
    <div
      className={`relative aspect-[2/3] w-full overflow-hidden bg-parchment ${slot.radius} ${slot.shadow} ${className}`}
      style={tilt}
    >
      {book.cover_url ? (
        <img
          src={book.cover_url}
          srcSet={coverSrcSet(book.cover_url)}
          sizes={slot.sizes}
          alt={decorative ? "" : book.title}
          width={slot.width}
          height={Math.round(slot.width * 1.5)}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
        />
      ) : (
        <CoverPlate book={book} size={size} />
      )}
    </div>
  );
}
